import isEqual from 'lodash/isEqual';

import { SDKVoiceFacade } from 'modules/sdk/lib';
import { SharedUserSettingsEnum, UserSettingsEnum, useSettingsStore } from 'modules/settings-sync/store';
import { useVoiceStore } from 'modules/voices/hooks';
import { voiceStoreActions } from 'modules/voices/stores/actions';
import { replaceFavoriteVoices } from 'modules/voices/stores/actions/setFavoriteVoices';
import { setVoiceHistory } from 'modules/voices/stores/actions/setSelectionHistory';
import { getVoiceByIDQualified } from 'modules/voices/utils/utils';

import { getIDQualified } from '../helpers';
import { queueSettingsChange } from '../service';

export function subscribe() {
  const remoteSettingsSyncChangesToSkip = new Set<string>();
  const localVoiceSyncChangesToSkip = new Set<string>();

  // remote settings store change, set local app voice
  const settingsSubscription = useSettingsStore.subscribe((state, prevState) => {
    const allVoices = SDKVoiceFacade.singleton.getAllVoiceInfos();

    const currentVoice = state.userSettings.settings[SharedUserSettingsEnum.CURRENT_VOICE] as string | undefined;
    const prevVoice = prevState.userSettings.settings[SharedUserSettingsEnum.CURRENT_VOICE] as string | undefined;
    const webAppVoice = state.userSettings.settings[SharedUserSettingsEnum.WEB_CURRENT_VOICE] as string | undefined;
    const currentRecentVoices = (state.userSettings.settings[SharedUserSettingsEnum.RECENT_VOICES] || []) as string[];
    const prevRecentVoices = (prevState.userSettings.settings[SharedUserSettingsEnum.RECENT_VOICES] || []) as string[];
    const currentFavoriteVoices = (state.userSettings.settings[SharedUserSettingsEnum.FAVORITE_VOICES] || []) as string[];
    const prevFavoriteVoices = (prevState.userSettings.settings[SharedUserSettingsEnum.FAVORITE_VOICES] || []) as string[];

    // remote current voice change
    if (currentVoice && !isEqual(currentVoice, prevVoice)) {
      // try to find voice by current voice ID first (new style ID)
      let voice = currentVoice ? getVoiceByIDQualified(currentVoice, allVoices) : undefined;

      // if not found and we have a platform-specific ID, try that
      if (!voice && webAppVoice) {
        voice = getVoiceByIDQualified(webAppVoice, allVoices);
      }

      if (voice) {
        const voiceId = getIDQualified(voice);

        if (voiceId && !localVoiceSyncChangesToSkip.has(voiceId)) {
          remoteSettingsSyncChangesToSkip.add(voiceId);
          voiceStoreActions.setVoice(voice, { shouldPlayPreview: false, isChangeFromSettingSync: true });
        }
      }

      localVoiceSyncChangesToSkip.clear();
    }

    // remote recent voices change
    if (!isEqual(currentRecentVoices, prevRecentVoices)) {
      const uniqueCurrentRecentVoices = [...new Set(currentRecentVoices)];
      const recentVoicesKey = uniqueCurrentRecentVoices.join(',');

      if (!localVoiceSyncChangesToSkip.has(recentVoicesKey)) {
        remoteSettingsSyncChangesToSkip.add(recentVoicesKey);
        setVoiceHistory(uniqueCurrentRecentVoices, allVoices);
      }

      localVoiceSyncChangesToSkip.clear();
    }

    // remote favorites voices change
    if (!isEqual(currentFavoriteVoices, prevFavoriteVoices)) {
      const uniqueCurrentFavoriteVoices = [...new Set(currentFavoriteVoices)];
      const favoriteVoicesKey = uniqueCurrentFavoriteVoices.join(',');

      if (!localVoiceSyncChangesToSkip.has(favoriteVoicesKey)) {
        remoteSettingsSyncChangesToSkip.add(favoriteVoicesKey);
        replaceFavoriteVoices(uniqueCurrentFavoriteVoices);
      }

      localVoiceSyncChangesToSkip.clear();
    }
  });

  // local voice store change, set remote settings voice
  const voiceSubscription = useVoiceStore.subscribe((state, prevState) => {
    const currentLastPlayingVoice = state.lastPlayingVoice;
    const currentRecentVoices = state.selectionHistory;
    const currentFavoriteVoices = state.favoriteVoices;

    const prevLastPlayingVoice = prevState.lastPlayingVoice;
    const prevRecentVoices = prevState.selectionHistory;
    const prevFavoriteVoices = prevState.favoriteVoices;

    const currentLastPlayingVoiceId = currentLastPlayingVoice ? currentLastPlayingVoice.slug || getIDQualified(currentLastPlayingVoice) : undefined;
    const prevLastPlayingVoiceId = prevLastPlayingVoice ? prevLastPlayingVoice.slug || getIDQualified(prevLastPlayingVoice) : undefined;

    // local last playing voice change
    if (currentLastPlayingVoiceId !== prevLastPlayingVoiceId) {
      if (currentLastPlayingVoiceId) {
        if (!remoteSettingsSyncChangesToSkip.has(currentLastPlayingVoiceId)) {
          localVoiceSyncChangesToSkip.add(currentLastPlayingVoiceId);

          const settings: Record<string, string> = {
            [UserSettingsEnum.CURRENT_VOICE]: currentLastPlayingVoiceId,
            [UserSettingsEnum.WEB_CURRENT_VOICE]: currentLastPlayingVoiceId
          };

          queueSettingsChange(settings);
        }

        remoteSettingsSyncChangesToSkip.clear();
      }
    }

    // local recent voices change
    if (currentRecentVoices?.length > 0 && !isEqual(currentRecentVoices, prevRecentVoices)) {
      if (!remoteSettingsSyncChangesToSkip.has(currentRecentVoices.join(','))) {
        localVoiceSyncChangesToSkip.add(currentRecentVoices.join(','));

        const recentVoiceIds = currentRecentVoices.map(voice => voice.slug || getIDQualified(voice));
        const uniqueRecentVoiceIds = [...new Set(recentVoiceIds)];

        queueSettingsChange({
          [UserSettingsEnum.RECENT_VOICES]: uniqueRecentVoiceIds
        });
      }

      remoteSettingsSyncChangesToSkip.clear();
    }

    // local favorite voices change
    if (!isEqual(currentFavoriteVoices, prevFavoriteVoices)) {
      const favoriteVoiceIds = currentFavoriteVoices.map(voice => voice.slug || getIDQualified(voice));
      const uniqueFavoriteVoiceIds = [...new Set(favoriteVoiceIds)];
      const favoriteVoicesKey = uniqueFavoriteVoiceIds.join(',');

      if (!remoteSettingsSyncChangesToSkip.has(favoriteVoicesKey)) {
        localVoiceSyncChangesToSkip.add(favoriteVoicesKey);

        queueSettingsChange({
          [SharedUserSettingsEnum.FAVORITE_VOICES]: uniqueFavoriteVoiceIds
        });
      }

      remoteSettingsSyncChangesToSkip.clear();
    }
  });

  // cleanup
  return () => {
    settingsSubscription?.();
    voiceSubscription?.();
  };
}
